// 58 §5b — the firm's schedule axis, edited. Six surfaces draw a week; this is
// where a firm says what its week IS, and `src/lib/scheduleAxis.ts` is where
// every one of them reads the answer.
//
// THE DEFAULTS ARE NOT WRITTEN ON OPEN. The panel renders the resolved axis,
// which with nothing stored is exactly today's constants — so opening it and
// changing nothing leaves `scheduleAxis` NULL and every surface unchanged. The
// firm is only committed by an edit.
//
// Module scope, no hook of its own beyond the local draft — CLAUDE.md's Bug A.
import { useState } from "react";
// 134 §2 — `RAD` left with the pill: the panel's only radius was its 20px one,
// and `.optToggle` carries `var(--rad-6)` in the stylesheet where it belongs.
import { FS } from "../lib/tokens";
import TimeField from "./TimeField"; // 117 §5
import MenuSelect from "./MenuSelect"; // 154B §2
import { resolveScheduleAxis, type ScheduleAxis } from "../lib/scheduleAxis";
import { observedCountries, availableHolidayCountries } from "../lib/legalHolidays"; // 132 §5
import { resolveCalendarDisplay, type CalendarDisplay } from "../lib/calendarDisplay"; // 133 §4
import { useTaxonomy, useTaxonomyList } from "../hooks/useTaxonomy";
import { resolveTaxonomyEntry } from "../lib/taxonomyHelpers";
import type { TFn } from "../lib/taxonomyTypes";
import Table from "./Table";

// 132 §5 — TWO INDEPENDENT PATCHES, never one. `scheduleAxis` is written WHOLE
// on every edit below; folding the country list into that object would mean a
// country change re-serialising the axis and an axis change preserving the
// countries. They are separate columns and separate writes.
// 133 §4 — and now THREE, for the same reason and no other: `calendarDisplay`
// is the third sibling column. See its migration for the enumeration.
type FirmSettingsPatch =
  | { scheduleAxis: unknown }
  | { holidayCountries: unknown }
  | { calendarDisplay: unknown };

type Props = {
  /** `scheduleAxis`, `holidayCountries`, `calendarDisplay` and `firm.country`
   *  are read; the rest of the row is not this panel's business */
  firmSettings:
    | {
        scheduleAxis?: unknown;
        holidayCountries?: unknown;
        calendarDisplay?: unknown;
        firm?: unknown;
      }
    | null
    | undefined;
  saveFirmSettings:
    | ((patch: FirmSettingsPatch) => Promise<unknown>)
    | null
    | undefined;
  t: TFn;
  toast: (msg: string) => void;
  canEdit: boolean;
};

const hhmm = (h: number) => `${String(h).padStart(2, "0")}:00`;

export default function ScheduleAxisPanel({
  firmSettings,
  saveFirmSettings,
  t,
  toast,
  canEdit,
}: Props) {
  const [busy, setBusy] = useState(false);
  const axis: ScheduleAxis = resolveScheduleAxis(firmSettings);

  /* 132 §5 — the country list, and the firm's own country as its default.
     `useTaxonomyList` gives the [code, label] tuples for the select; the
     ENTRIES go to `resolveTaxonomyEntry` — `firm.country` is a CODE since 207
     §12, read by its code facet, and a text not yet converted by its labels;
     05m ruled that five-facet match the one matcher. */
  const countryTuples = useTaxonomyList("country") as [string, string][];
  const countryEntries = useTaxonomy("country").entries;
  /* THE OFFER LIST IS THE SEED'S, NOT ISO'S. §4 ships Belgium only, so the ISO
     taxonomy would offer ~250 countries of which 249 draw nothing. This grows
     by itself when the seed does. `legal_holiday` is a SEED_ONLY kind, so an
     unseeded box yields [] rather than a bundled list — and the panel says so
     rather than showing an empty row of pills. */
  const offered = availableHolidayCountries(useTaxonomy("legal_holiday").entries);
  const countryLabel = (code: string) =>
    countryTuples.find(([c]) => String(c).toUpperCase() === code)?.[1] || code;
  const firmCountry = (firmSettings?.firm as { country?: unknown } | null)?.country;
  const observed = observedCountries(firmSettings?.holidayCountries, firmCountry, (v) =>
    resolveTaxonomyEntry(v as never, countryEntries as never),
  );
  /* NULL IS NOT []. A firm that has never answered is SHOWING a derived
     default, not storing one — the same posture the axis has, where opening
     this panel and changing nothing leaves the column NULL. */
  const derived = !Array.isArray(firmSettings?.holidayCountries);

  const pushCountries = (next: string[]) => {
    if (!saveFirmSettings || busy) return;
    setBusy(true);
    // Its OWN patch. See the FirmSettingsPatch note above.
    saveFirmSettings({ holidayCountries: next })
      .catch((e: unknown) =>
        toast(
          t("settings.schedAxis.save_failed", {
            msg: (e instanceof Error && e.message) || "",
            fallback: "⚠ Could not save the schedule axis: {msg}",
          }),
        ),
      )
      .finally(() => setBusy(false));
  };

  /* 133 §4 — the calendar-display switches. Read through the same resolver the
     four drawing surfaces read (`src/lib/calendarDisplay.ts`), so the panel can
     never show a state the screens do not have. */
  const calDisp = resolveCalendarDisplay(firmSettings);
  const pushCalendar = (patch: Partial<CalendarDisplay>) => {
    if (!saveFirmSettings || busy) return;
    setBusy(true);
    /* THE WHOLE OBJECT, from the RESOLVED value plus this change. `{...calDisp,
       ...patch}` writes the two switches the firm did not touch at their
       current values rather than leaving them absent — which is what makes a
       later change of DEFAULT safe: a firm that has answered has answered all
       three, and 132 §3's lesson is that a default cannot reach a firm that
       already answered. Its OWN patch, never folded into the axis. */
    saveFirmSettings({ calendarDisplay: { ...calDisp, ...patch } })
      .catch((e: unknown) =>
        toast(
          t("settings.schedAxis.save_failed", {
            msg: (e instanceof Error && e.message) || "",
            fallback: "⚠ Could not save the schedule axis: {msg}",
          }),
        ),
      )
      .finally(() => setBusy(false));
  };

  const push = (next: ScheduleAxis) => {
    if (!saveFirmSettings || busy) return;
    setBusy(true);
    saveFirmSettings({
      scheduleAxis: {
        start: next.start,
        end: next.end,
        days: next.days.map((d) => ({ code: d.code, working: d.working, width: d.width })),
      },
    })
      .catch((e: unknown) =>
        toast(
          t("settings.schedAxis.save_failed", {
            msg: (e instanceof Error && e.message) || "",
            fallback: "⚠ Could not save the schedule axis: {msg}",
          }),
        ),
      )
      .finally(() => setBusy(false));
  };

  const setDay = (i: number, patch: { working?: boolean; width?: number }) =>
    push({ ...axis, days: axis.days.map((d, k) => (k === i ? { ...d, ...patch } : d)) });

  const setHour = (which: "start" | "end", v: number) => {
    const next = { ...axis, [which]: v } as ScheduleAxis;
    // The resolver falls back on an inverted range rather than rendering a
    // blank grid; refusing here means the firm is told instead of silently
    // getting the constant back.
    if (next.end <= next.start) {
      toast(
        t("settings.schedAxis.bad_range", {
          fallback: "⚠ The day must end after it starts.",
        }),
      );
      return;
    }
    push(next);
  };

  return (
    <>
      <p style={{ fontSize: FS.s11, color: "var(--tm)", margin: "4px 0 10px", lineHeight: 1.5 }}>
        {/* 132 §3/§5 — A NEW KEY, not an edited one: the seed only INSERTS, so
            a changed string never reaches a database that already holds the old
            row. The sentence had to change twice over — `width` no longer
            reaches the two matter lists or the dashboard (§3 ruled every day a
            full column there, and Places is its only remaining reader), and the
            panel now carries the holiday countries too. */}
        {t("settings.schedAxis.desc2", {
          fallback:
            "Which days the firm works, the hours the schedule grids cover, and which countries' legal holidays it observes. Column width applies to the Places availability overview; the matter lists and the dashboard draw every day at full width.",
        })}
      </p>
      <div className="field-row">
        <div className="field">
          <label>{t("settings.label.schedStart", { fallback: "Day starts" })}</label>
          {/* BRIEF 117 §5 — `step={60}` MINUTES, not 3600 seconds: this axis
              is whole hours and throws the minute half away on arrival
              (`slice(0, 2)` below), so its minute column offers 00 alone. */}
          <TimeField
            step={60}
            disabled={!canEdit || busy}
            value={hhmm(axis.start)}
            onChange={(v) => setHour("start", Number(v.slice(0, 2)))}
          />
        </div>
        <div className="field">
          <label>{t("settings.label.schedEnd", { fallback: "Day ends" })}</label>
          <TimeField
            step={60}
            disabled={!canEdit || busy}
            value={hhmm(axis.end)}
            onChange={(v) => setHour("end", Number(v.slice(0, 2)))}
          />
        </div>
      </div>
      <Table style={{ width: "100%", fontSize: FS.s11, marginTop: 8 }}>
        <tbody>
          {axis.days.map((d, i) => (
            <tr key={d.code}>
              <td style={{ padding: "3px 0", color: "var(--ts)", width: 64 }}>{d.code}</td>
              <td style={{ padding: "3px 0" }}>
                {/* 134 §2 — the same `.optToggle` the countries below now take:
                    the panel had this vocabulary already and the pill was the
                    one control not speaking it. Hover and focus arrive here
                    too, because a hover state on the countries ALONE would be
                    the third treatment §2 forbids. */}
                <label
                  className={"optToggle" + (d.working ? " is-on" : "") + (!canEdit || busy ? " is-off" : "")}
                >
                  <input
                    type="checkbox"
                    disabled={!canEdit || busy}
                    checked={d.working}
                    onChange={(e) => setDay(i, { working: e.target.checked })}
                  />
                  {t("settings.schedAxis.working", { fallback: "Working day" })}
                </label>
              </td>
              <td style={{ padding: "3px 0", textAlign: "right" }}>
                {/* 154B §2 — `display: inline-block` becomes `inline-flex`:
                    MenuSelect's trigger lays its label and caret out with flex
                    and spreads the caller's style AFTER its own, so a plain
                    `inline-block` would win and collapse that row. The box is
                    the same inline-level 130px it was. */}
                <MenuSelect
                  className="field-input"
                  style={{ width: 130, display: "inline-flex" }}
                  disabled={!canEdit || busy}
                  value={String(d.width)}
                  onChange={(v) => setDay(i, { width: Number(v) })}
                  options={[
                    ["2", t("settings.schedAxis.full", { fallback: "Full column" })],
                    ["1", t("settings.schedAxis.half", { fallback: "Half column" })],
                  ]}
                />
              </td>
            </tr>
          ))}
        </tbody>
      </Table>

      {/* ── 132 §5 — WHICH COUNTRIES' LEGAL HOLIDAYS THE FIRM OBSERVES ──────
          IT IS IN THIS PANEL AND NOT A NEW ONE. §5 measured that the weekday
          flags and widths above are ALREADY the answer to "which weekdays are
          weekends", so nothing here builds a second weekday control. What was
          missing was the holidays, and they belong beside the days. */}
      <div style={{ marginTop: 14, borderTop: "1px solid var(--br)", paddingTop: 10 }}>
        <label style={{ display: "block" }}>
          {t("settings.holidays.countries", { fallback: "Legal holidays observed" })}
        </label>
        <p style={{ fontSize: FS.s10_5, color: "var(--tm)", margin: "2px 0 8px", lineHeight: 1.5 }}>
          {derived
            ? t("settings.holidays.derived", {
                fallback:
                  "Defaulting to the firm's own country. Choose explicitly to change it; choosing none draws no holidays.",
              })
            : t("settings.holidays.desc", {
                fallback:
                  "Holidays of these countries are drawn like non-working days. Choosing none draws no holidays.",
              })}
        </p>
        {offered.length === 0 ? (
          <p style={{ fontSize: FS.s10_5, color: "var(--am)", margin: 0, lineHeight: 1.5 }}>
            {t("settings.holidays.none_seeded", {
              fallback:
                "No holiday data is available yet — nothing is drawn. This is not an error: the reference rows have not been seeded on this instance.",
            })}
          </p>
        ) : (
          <div style={{ display: "flex", flexWrap: "wrap", gap: 5 }}>
            {/* Any code the firm ALREADY observes is offered even if the seed no
                longer carries it, so a stored answer can always be turned off. */}
            {/* ── 134 §2 — IT IS A CONTROL NOW, AND IT LOOKS LIKE ONE ───────
                Laurent: "just text, not SHOWING as a button per app styling."
                It was a pill — 20px radius, 10px type, a pale wash — which is
                this app's READ-ONLY STATUS vocabulary (*Paid*, *Pending*,
                *Overdue*), and with one country offered there was no
                unselected neighbour to contrast it against, so it read as a
                label rather than something to click.

                The census and its verdict are in `.optToggle`'s own comment in
                App.css: this panel ALREADY answers "pick several from a set"
                with a checkbox and a label, four rows above, and again in 133
                §4's three switches. Adopting it costs nothing and the panel now
                speaks one vocabulary — which is also why the class goes on all
                three groups rather than on this one alone. */}
            {Array.from(new Set([...offered, ...observed])).map((code) => {
              const on = observed.includes(code);
              const locked = !canEdit || busy;
              return (
                <label
                  key={code}
                  className={"optToggle" + (on ? " is-on" : "") + (locked ? " is-off" : "")}
                  style={{ fontSize: FS.s10_5 }}
                >
                  <input
                    type="checkbox"
                    disabled={locked}
                    checked={on}
                    onChange={() =>
                      /* The first click on a DERIVED list commits that list plus
                         or minus this one — never an empty array by accident. */
                      pushCountries(on ? observed.filter((x) => x !== code) : [...observed, code])
                    }
                  />
                  {countryLabel(code)}
                </label>
              );
            })}
          </div>
        )}
      </div>

      {/* ── 133 §4 — HOW THE CALENDAR DRAWS ────────────────────────────────
          IT IS IN THIS PANEL AND NOT A NEW ONE. §4: "Do not start a second
          settings home." The two neighbours are already here — which weekdays
          the firm works, and whose holidays it observes — and these three
          switches decide how those same two answers are DRAWN. A firm asking
          "why is Saturday grey" is one scroll from the setting.

          THREE CHECKBOXES AND NOT A SELECT, because they are independent: a
          firm may want the strips marked and the agenda plain, or the band
          back with no shading anywhere. Same control the weekday flags above
          use, so the panel keeps one vocabulary.

          EVERY DEFAULT REPRODUCES WHAT 133 §2 AND 132 §7 RULE. Opening this
          panel and changing nothing leaves the column NULL — the same posture
          the axis and the country list have — so nothing here changes a screen
          until somebody clicks. */}
      <div style={{ marginTop: 14, borderTop: "1px solid var(--br)", paddingTop: 10 }}>
        <label style={{ display: "block" }}>
          {t("settings.calDisp.group", { fallback: "Calendar display" })}
        </label>
        <p style={{ fontSize: FS.s10_5, color: "var(--tm)", margin: "2px 0 8px", lineHeight: 1.5 }}>
          {t("settings.calDisp.desc", {
            fallback:
              "How non-working days and holidays are marked on the day strips and in the agenda.",
          })}
        </p>
        {(
          [
            [
              "stripNonWorking",
              t("settings.calDisp.strip", {
                fallback: "Shade non-working days on the 14-day strips",
              }),
            ],
            [
              "agendaNonWorking",
              t("settings.calDisp.agenda", {
                fallback: "Shade non-working days in the agenda",
              }),
            ],
            [
              "agendaAltBand",
              t("settings.calDisp.band", {
                fallback: "Shade every other day in the agenda",
              }),
            ],
          ] as [keyof CalendarDisplay, string][]
        ).map(([k, label]) => (
          /* 134 §2 — `.optToggle`, like the weekday flags this comment's own
             133 §4 text says it copies, and like the countries. These three sit
             one scroll below the pill Laurent read as a label, so leaving them
             on a fourth spelling would answer his complaint in one place and
             re-open it in another. `display: flex` stays a full-width row —
             they are stacked statements, not a wrapped strip. */
          <label
            key={k}
            className={"optToggle" + (calDisp[k] ? " is-on" : "") + (!canEdit || busy ? " is-off" : "")}
            style={{ display: "flex", fontSize: FS.s11 }}
          >
            <input
              type="checkbox"
              disabled={!canEdit || busy}
              checked={calDisp[k]}
              onChange={(e) => pushCalendar({ [k]: e.target.checked })}
            />
            <span>{label}</span>
          </label>
        ))}
      </div>
    </>
  );
}
